import { useEffect, useState, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import ActionButton from "@/components/ActionButton";
import api, { formatEur, formatEur2, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { Plus, RefreshCw, Plug, CheckCircle2, AlertCircle, ShoppingCart, Mail, Phone, MapPin, Package, Wallet } from "lucide-react";
import FournisseurOrderModal from "@/components/FournisseurOrderModal";

const STATUS = {
  commande_client: "Commande client",
  paye: "Payé",
  commande_fournisseur: "Cmd. fournisseur",
  en_livraison: "En livraison",
  livre: "Livré",
  clos: "Clos",
};
const STATUS_CLS = {
  commande_client: "bg-amber-100 text-amber-800",
  paye: "bg-emerald-100 text-emerald-800",
  commande_fournisseur: "bg-blue-100 text-blue-800",
  en_livraison: "bg-indigo-100 text-indigo-800",
  livre: "bg-stone-200 text-stone-700",
  clos: "bg-stone-100 text-stone-500",
};
const FLOW = ["commande_client", "paye", "commande_fournisseur", "en_livraison", "livre", "clos"];

export default function Franchimob() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [franchiseurs, setFranchiseurs] = useState([]);
  const [show, setShow] = useState(false);
  const [wooStatus, setWooStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [supplierOrder, setSupplierOrder] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/franchimob");
    setList(data);
  }, []);
  const loadFranchiseurs = useCallback(async () => {
    try { const { data } = await api.get("/franchiseurs/list"); setFranchiseurs(data || []); } catch (e) { /* ignore */ }
  }, []);
  const loadStatus = useCallback(async () => {
    try { const { data } = await api.get("/woocommerce/status"); setWooStatus(data); } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    load(); loadStatus(); loadFranchiseurs();
    const t = setInterval(load, 30000); // suivi quasi temps réel
    return () => clearInterval(t);
  }, [load, loadStatus, loadFranchiseurs]);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/woocommerce/sync", {});
      toast.success(`${data.imported} commande(s) importée(s), ${data.factures_generated} facture(s) générée(s)`);
      await load(); await loadStatus();
    } catch (e) { /* toast via interceptor */ } finally { setSyncing(false); }
  };

  const create = async (e) => {
    e.preventDefault();
    const fd = Object.fromEntries(new FormData(e.target).entries());
    fd.montant_vente = parseFloat(fd.montant_vente || 0);
    fd.montant_achat = parseFloat(fd.montant_achat || 0);
    await api.post("/franchimob", fd);
    setShow(false);
    load();
  };

  const setStatus = async (id, status) => {
    await api.put(`/franchimob/${id}`, { status });
    load();
  };

  const setFranchiseur = async (id, franchiseur) => {
    await api.put(`/franchimob/${id}`, { franchiseur });
    load(); loadFranchiseurs();
  };

  const addFranchiseur = async (id) => {
    const nom = window.prompt("Nom du nouveau franchiseur :");
    if (!nom || !nom.trim()) { load(); return; }  // annulation → re-render pour réinitialiser le select
    try {
      await api.post("/franchiseurs/upsert-by-name", { nom: nom.trim(), taux_retrocommission: 10 });
      await loadFranchiseurs();
      await api.put(`/franchimob/${id}`, { franchiseur: nom.trim() });
      toast.success(`Franchiseur « ${nom.trim()} » créé et rattaché`);
      load();
    } catch (e) { /* toast via interceptor */ }
  };

  const goToRetro = (franchiseur) => {
    navigate(`/franchiseurs/${encodeURIComponent(franchiseur)}?tab=retrocommissions`);
  };

  const connected = wooStatus && (wooStatus.webhook_configured || wooStatus.rest_configured);
  const aTraiter = list.filter((o) => o.status === "paye").length;

  return (
    <div className="space-y-6" data-testid="fmob-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <span className="pill chip-fmob">Franchi'Mob</span>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-2">Commandes</h1>
        </div>
        <div className="flex items-center gap-2">
          <ActionButton type="button" onClick={sync} disabled={!wooStatus?.rest_configured || syncing} data-testid="woo-sync-btn" className="px-3 py-2 text-sm border border-stone-300 rounded font-semibold flex items-center gap-2 disabled:opacity-50">
            <RefreshCw size={15} className={syncing ? "animate-spin" : ""}/> Synchroniser
          </ActionButton>
          <button data-testid="fmob-new" onClick={() => setShow(true)} className="bg-[#1E3A8A] text-white px-4 py-2 rounded text-sm font-semibold flex items-center gap-2"><Plus size={16}/> Nouvelle commande</button>
        </div>
      </div>

      {/* Bandeau connexion WooCommerce */}
      <div data-testid="woo-status-banner" className={`card-flat p-4 flex items-center gap-3 flex-wrap border-l-4 ${connected ? "border-emerald-500" : "border-amber-400"}`}>
        <Plug size={18} className={connected ? "text-emerald-600" : "text-amber-500"}/>
        <div className="flex-1 min-w-[200px]">
          <div className="font-semibold text-sm flex items-center gap-2">
            WooCommerce {connected
              ? <span className="text-emerald-700 flex items-center gap-1"><CheckCircle2 size={14}/> Connecté</span>
              : <span className="text-amber-700 flex items-center gap-1"><AlertCircle size={14}/> Non connecté</span>}
          </div>
          <div className="text-xs text-stone-500 mt-0.5">
            {connected
              ? `${wooStatus.orders_imported} commande(s) importée(s) · ${wooStatus.factures_generated} facture(s) générée(s)${wooStatus.base_url ? " · " + wooStatus.base_url : ""}`
              : "En attente des clés API (Consumer Key/Secret + secret webhook). La synchro et la facturation auto s'activeront une fois configurées."}
          </div>
        </div>
        {aTraiter > 0 && <span className="pill bg-emerald-600 text-white text-xs flex items-center gap-1" data-testid="woo-a-traiter"><ShoppingCart size={12}/> {aTraiter} payée(s) à traiter</span>}
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500 border-b">
            <tr><th className="px-4 py-3 text-left">N°</th><th className="px-4 py-3 text-left">Client</th><th className="px-4 py-3 text-left">Produit</th><th className="px-4 py-3 text-left">Source</th><th className="px-4 py-3 text-right">Vente</th><th className="px-4 py-3 text-left">Franchiseur</th><th className="px-4 py-3 text-left">Statut</th><th className="px-4 py-3 text-right">Rétro 10%</th><th></th></tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-stone-400 italic">Aucune commande. Les commandes WooCommerce apparaîtront ici automatiquement une fois la connexion active.</td></tr>
            )}
            {list.map((o) => {
              const isWoo = !!o.woo_order_id;
              const open = expanded === o.id;
              const retro = o.retro || {};
              return (
                <Fragment key={o.id}>
                  <tr className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => isWoo && setExpanded(open ? null : o.id)} data-testid={`fmob-row-${o.id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{o.numero}</td>
                    <td className="px-4 py-3">
                      <div>{o.client}</div>
                      {isWoo && o.client_email && <div className="text-[11px] text-stone-400 flex items-center gap-1"><Mail size={10}/> {o.client_email}</div>}
                    </td>
                    <td className="px-4 py-3 text-stone-600 max-w-[220px] truncate">{o.produit}</td>
                    <td className="px-4 py-3">
                      {isWoo
                        ? <span className="pill bg-[#7F54B3] text-white text-[10px]">WooCommerce</span>
                        : <span className="pill bg-stone-100 text-stone-500 text-[10px]">Manuel</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{formatEur(o.montant_vente)}</td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <select
                        data-testid={`fmob-franchiseur-${o.id}`}
                        value={o.franchiseur || ""}
                        onChange={(e) => { const v = e.target.value; if (v === "__new__") addFranchiseur(o.id); else setFranchiseur(o.id, v); }}
                        className="border border-stone-200 rounded px-2 py-1 text-xs max-w-[160px]">
                        <option value="">— Aucun —</option>
                        {franchiseurs.map((f) => <option key={f.id || f.nom} value={f.nom}>{f.nom}</option>)}
                        <option value="__new__">+ Nouveau franchiseur…</option>
                      </select>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <select
                        data-testid={`fmob-status-${o.id}`}
                        value={o.status}
                        onChange={(e) => setStatus(o.id, e.target.value)}
                        className={`rounded px-2 py-1 text-[11px] font-semibold border-0 ${STATUS_CLS[o.status] || "bg-stone-100"}`}>
                        {FLOW.map((s) => <option key={s} value={s}>{STATUS[s]}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {o.franchiseur ? (
                        <button
                          data-testid={`fmob-retro-link-${o.id}`}
                          onClick={(e) => { e.stopPropagation(); goToRetro(o.franchiseur); }}
                          className={`font-mono text-xs inline-flex items-center gap-1 hover:underline ${retro.status === "payee" ? "text-emerald-600" : retro.status === "due" ? "text-amber-700" : "text-stone-500"}`}
                          title="Voir les rétrocommissions du franchiseur">
                          <Wallet size={12}/> {formatEur2(retro.montant || 0)}
                        </button>
                      ) : (
                        <span className="text-[11px] text-stone-300 italic">rattacher</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3 justify-end">
                        <button data-testid={`fmob-fournisseurs-${o.id}`} onClick={(e) => { e.stopPropagation(); setSupplierOrder(o); }} className="text-xs text-[#C2502F] hover:underline flex items-center gap-1"><Package size={13}/> Fournisseurs</button>
                      </div>
                    </td>
                  </tr>
                  {open && isWoo && (
                    <tr className="bg-stone-50/70" data-testid={`fmob-detail-${o.id}`}>
                      <td colSpan={9} className="px-6 py-3 text-xs text-stone-600">
                        <div className="grid md:grid-cols-2 gap-x-8 gap-y-1">
                          {o.client_tel && <div className="flex items-center gap-1.5"><Phone size={12} className="text-stone-400"/> {o.client_tel}</div>}
                          {o.contact_livraison && <div>Contact livraison : <span className="font-medium">{o.contact_livraison}</span></div>}
                          {o.adresse_facturation && <div className="flex items-start gap-1.5"><MapPin size={12} className="text-stone-400 mt-0.5"/> Facturation : {o.adresse_facturation}</div>}
                          {o.adresse_livraison && <div className="flex items-start gap-1.5"><MapPin size={12} className="text-stone-400 mt-0.5"/> Livraison : {o.adresse_livraison}</div>}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {show && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form onSubmit={create} className="bg-white rounded-lg p-6 max-w-lg w-full" data-testid="fmob-form">
            <h3 className="font-display text-2xl font-bold mb-4">Nouvelle commande</h3>
            <div className="space-y-3 text-sm">
              <input name="numero" required placeholder="N° commande" className="w-full border border-gray-300 rounded px-3 py-2"/>
              <input name="client" required placeholder="Client" className="w-full border border-gray-300 rounded px-3 py-2"/>
              <input name="produit" required placeholder="Produit" className="w-full border border-gray-300 rounded px-3 py-2"/>
              <input name="fournisseur" placeholder="Fournisseur" className="w-full border border-gray-300 rounded px-3 py-2"/>
              <select name="franchiseur" data-testid="fmob-form-franchiseur" className="w-full border border-gray-300 rounded px-3 py-2">
                <option value="">Franchiseur (optionnel)</option>
                {franchiseurs.map((f) => <option key={f.id || f.nom} value={f.nom}>{f.nom}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-2">
                <input name="montant_vente" type="number" placeholder="Vente €" className="border border-gray-300 rounded px-3 py-2"/>
                <input name="montant_achat" type="number" placeholder="Achat €" className="border border-gray-300 rounded px-3 py-2"/>
              </div>
            </div>
            <div className="flex gap-2 mt-5 justify-end">
              <button type="button" onClick={() => setShow(false)} className="px-4 py-2 text-sm border rounded">Annuler</button>
              <ActionButton type="button" onClick={create} data-testid="fmob-submit" successLabel="Créé" className="px-4 py-2 text-sm bg-[#1E3A8A] text-white rounded font-semibold">Créer</ActionButton>
            </div>
          </form>
        </div>
      )}
      {supplierOrder && <FournisseurOrderModal order={supplierOrder} onClose={() => setSupplierOrder(null)} onChanged={load}/>}
    </div>
  );
}
